import { useEffect, useRef, useState } from 'react';
import {
  DRAFT_STORAGE_KEY,
  INTRO_COPY,
  createInitialFormState,
} from '../constants/formConfig';
import { submitRegistration } from '../services/registrationApi';
import { downloadRegistrationPdf } from '../utils/pdfExport';
import {
  hasErrors,
  validatePage1,
  validatePage2,
} from '../utils/validation';
import { FormHeader } from './FormHeader';
import { PageProgress } from './PageProgress';
import { SchoolContacts } from './SchoolContacts';
import { SchoolInformation } from './SchoolInformation';
import { StudentRegistration } from './StudentRegistration';
import { TermsAndPayment } from './TermsAndPayment';

function loadDraft() {
  try {
    const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function RegistrationForm() {
  const [page, setPage] = useState(1);
  const [form, setForm] = useState(() => loadDraft()?.form || createInitialFormState());
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [saveMessage, setSaveMessage] = useState('');
  const [result, setResult] = useState(null);
  const [codeConflict, setCodeConflict] = useState(false);
  const paymentRef = useRef(null);

  useEffect(() => {
    const draft = loadDraft();
    if (draft?.page) setPage(draft.page);
  }, []);

  const patchForm = (partial) => {
    setForm((prev) => ({ ...prev, ...partial }));
  };

  const saveDraft = () => {
    localStorage.setItem(
      DRAFT_STORAGE_KEY,
      JSON.stringify({ form, page, savedAt: new Date().toISOString() }),
    );
    setSaveMessage('Progress saved on this device.');
    window.setTimeout(() => setSaveMessage(''), 2500);
  };

  const goNext = () => {
    const nextErrors = validatePage1(form);
    if (codeConflict) {
      nextErrors.schoolCode =
        'This school code is already registered to another school. Please choose a different code.';
    }
    setErrors(nextErrors);
    if (!hasErrors(nextErrors)) {
      setPage(2);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goBack = () => {
    setErrors({});
    setSubmitError('');
    setPage(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError('');

    const nextErrors = validatePage2(form);
    setErrors(nextErrors);
    if (hasErrors(nextErrors)) {
      // The student-limit message also appears next to Register, where the click happened.
      if (nextErrors.students) setSubmitError(nextErrors.students);
      if (Object.keys(nextErrors).some((key) => key !== 'students')) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return;
    }

    setSubmitting(true);
    try {
      const payment = await paymentRef.current?.confirmPayment();
      if (!payment?.ok) {
        setSubmitError(payment?.error || 'Payment could not be completed.');
        setSubmitting(false);
        return;
      }

      const response = await submitRegistration(form, {
        status: payment.status,
        method: payment.method,
        accessCode: payment.accessCode,
        checkNumber: payment.checkNumber,
        paymentIntentId: payment.paymentIntentId,
        paymentMethodId: payment.paymentMethodId,
      });

      localStorage.removeItem(DRAFT_STORAGE_KEY);
      setResult(response);
    } catch (error) {
      setSubmitError(error.message || 'Something went wrong while submitting.');
    } finally {
      setSubmitting(false);
    }
  };

  if (result?.ok) {
    return (
      <div className="registration-page">
        <div className="registration-shell">
          <FormHeader />
          <div className="registration-card success-panel">
            <h1>Registration submitted</h1>
            <p>
              {result.message ||
                'Your school registration has been submitted successfully.'}
              {result.mode === 'local'
                ? ' Download the PDF below.'
                : ' A confirmation PDF is ready to download.'}
            </p>
            <div className="success-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() =>
                  downloadRegistrationPdf(form, { payment: result.payment })
                }
              >
                Download submission PDF
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setResult(null);
                  setForm(createInitialFormState());
                  setErrors({});
                  setPage(1);
                }}
              >
                Register another school
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="registration-page">
      <div className="registration-shell">
        <FormHeader />

        <form className="registration-card" onSubmit={handleSubmit} noValidate>
          <div className="form-top">
            <h1 className="form-title">School Registration</h1>
            <PageProgress page={page} />
          </div>

          <div className="intro-block">
            <p>{INTRO_COPY.lead}</p>
            <p className="intro-support">{INTRO_COPY.support}</p>
          </div>

          {page === 1 ? (
            <>
              <SchoolInformation
                school={form.school}
                errors={errors}
                onChange={(school) => patchForm({ school })}
                onCodeConflict={setCodeConflict}
              />
              <SchoolContacts
                contacts={form.contacts}
                errors={errors}
                onChange={(contacts) => patchForm({ contacts })}
              />
              {saveMessage ? (
                <p className="save-message">{saveMessage}</p>
              ) : null}
              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={saveDraft}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={goNext}
                >
                  Next
                </button>
              </div>
              <p className="page-footer-meta">1/2</p>
            </>
          ) : (
            <>
              <StudentRegistration
                form={form}
                errors={errors}
                onChange={patchForm}
              />
              <TermsAndPayment
                form={form}
                errors={errors}
                onChange={patchForm}
                paymentRef={paymentRef}
              />

              {submitError ? (
                <p className="field-error" style={{ marginBottom: 12 }}>
                  {submitError}
                </p>
              ) : null}
              {saveMessage ? (
                <p className="save-message">{saveMessage}</p>
              ) : null}

              <div className="form-actions three">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={goBack}
                  disabled={submitting}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={saveDraft}
                  disabled={submitting}
                >
                  Save
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitting}
                >
                  {submitting ? 'Registering…' : 'Register'}
                </button>
              </div>
              <p className="page-footer-meta">2/2</p>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
